import { Auth } from 'aws-amplify';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import logo from '../images/rustle_it_logo.png';
import { useAppState } from '../context/AppStateContext';
import { UserDataService } from '../services/userDataService';

const Login: React.FC = () => {
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userChallenge, setUserChallenge] = useState<any>(null);

  // Forgot-password flow. 'request' = ask Cognito to email a code;
  // 'reset' = enter that code + choose a new password. resetCode holds the
  // emailed verification code. Reuses username / newPassword / confirmPassword.
  const [forgotMode, setForgotMode] = useState<'request' | 'reset' | null>(null);
  const [resetCode, setResetCode] = useState('');

  // MFA (TOTP) state. mfaMode 'verify' = enrolled user entering a code;
  // 'setup' = first-time enrolment (show QR + verify). mfaUser is the
  // CognitoUser returned by the sign-in challenge.
  const [mfaUser, setMfaUser] = useState<any>(null);
  const [mfaMode, setMfaMode] = useState<'verify' | 'setup' | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [totpUri, setTotpUri] = useState('');
  const codeInputRef = useRef<HTMLInputElement>(null);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isInProgress, setIsInProgress] = useState<boolean>(false);

  const [error, setError] = useState<React.ReactNode>('');

  // One-off notice surfaced after an involuntary sign-out (e.g. PIN reset).
  // Stashed in sessionStorage by the axios 401 handler; read once and cleared.
  const [notice, setNotice] = useState<string>('');

  const {user, setUser, childProfiles, setChildProfiles } = useAppState();

  useEffect(() => {
    const msg = sessionStorage.getItem('authMessage');
    if (msg) {
      setNotice(msg);
      sessionStorage.removeItem('authMessage');
    }
  }, []);

  // Redirect if already signed in
  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then(async () => {
        const { hasPin } = await UserDataService.checkPinSet();
        navigate(hasPin ? '/select-org' : '/setup-pin');
      })
      .catch(() => {
        // User not signed in; stay on login page
        setIsLoading(false);
      });
  }, []);

  // Focus the code field whenever the MFA code/setup prompt appears.
  useEffect(() => {
    if (mfaMode === 'verify' || mfaMode === 'setup') {
      const t = setTimeout(() => codeInputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [mfaMode]);

  // Shared post-authentication step. Marks THIS browser as a remembered
  // ("trusted") device so Cognito skips MFA on future sign-ins from it, then
  // routes by PIN status. WEB ONLY — the iOS app deliberately does not remember
  // devices, so shared iPads are MFA-challenged each day. rememberDevice is
  // best-effort: it no-ops if device tracking is off and must never block login.
  const afterSignedIn = async () => {
    try { await Auth.rememberDevice(); } catch { /* non-fatal: device tracking off / already remembered */ }
    // Resilient: we are authenticated here, so a transient checkPinSet failure
    // must not bubble up and be mistaken for a sign-in failure. Default to the
    // org-select landing and let the downstream guards route.
    let hasPin = true;
    try { ({ hasPin } = await UserDataService.checkPinSet()); } catch { /* default below */ }
    navigate(hasPin ? '/select-org' : '/setup-pin');
  };

  // Route a CognitoUser returned by signIn / completeNewPassword / confirmSignIn
  // based on any pending challenge (new password, MFA code, or first-time TOTP
  // setup). No pending challenge → the user is signed in, so proceed.
  const handleSignInResult = async (u: any) => {
    switch (u?.challengeName) {
      case 'NEW_PASSWORD_REQUIRED':
        setUserChallenge(u);
        setIsInProgress(false);
        break;
      case 'SMS_MFA':
      case 'SOFTWARE_TOKEN_MFA':
        // Enrolled user → ask for the 6-digit code.
        setMfaUser(u);
        setMfaMode('verify');
        setMfaCode('');
        setIsInProgress(false);
        break;
      case 'MFA_SETUP': {
        // Not yet enrolled → start TOTP setup. setupTOTP returns the shared
        // secret; build the otpauth URI for the QR code and manual entry.
        const secret = await Auth.setupTOTP(u);
        const issuer = 'Rustle It';
        const account = (typeof u.getUsername === 'function' ? u.getUsername() : username) || 'user';
        setMfaUser(u);
        setTotpSecret(secret);
        setTotpUri(`otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(account)}?secret=${secret}&issuer=${encodeURIComponent(issuer)}`);
        setMfaMode('setup');
        setMfaCode('');
        setIsInProgress(false);
        break;
      }
      default:
        await afterSignedIn();
    }
  };

  const doLogin = async () => {
    setError('');
    setIsInProgress(true);
    try {
      const u = await Auth.signIn(username, password);
      await handleSignInResult(u);
    } catch (err: any) {
      setIsInProgress(false);
      setError(err.message || 'Login failed');
      console.error('Login failed');
    }
  };

  // Enrolled user submitting their authenticator code.
  const handleMfaVerify = async () => {
    setError('');
    setIsInProgress(true);

    try {
      await Auth.confirmSignIn(mfaUser, mfaCode.replace(/\D/g, ''), mfaUser.challengeName);
      // confirmSignIn resolved → the code was accepted and sign-in is complete.
      // Do NOT route the result through handleSignInResult: the returned user
      // object can still carry the old `challengeName` ('SOFTWARE_TOKEN_MFA'),
      // which would send us straight back to the code screen — the re-prompt
      // loop. Just proceed.
      await afterSignedIn();
      return;
    } catch (err: any) {
      const code = err?.code || err?.name || 'Error';
      console.error('MFA verify error:', code, err?.message);

      // Amplify can REJECT confirmSignIn even after authentication has actually
      // succeeded — notably the device-tracking / "remember device"
      // post-processing step. If we are in fact signed in, proceed; do NOT
      // treat it as a code failure (that was the re-prompt loop).
      try {
        await Auth.currentAuthenticatedUser();
        await afterSignedIn();
        return;
      } catch { /* genuinely not authenticated → a real challenge failure */ }

      // Genuine failure: surface the real reason and return to sign-in.
      setMfaCode('');
      setMfaUser(null);
      setMfaMode(null);
      setIsInProgress(false);
      setError(`Sign-in couldn’t be completed (${code}). ${err?.message || ''} Please sign in again.`);
    }
  };

  // First-time enrolment: verify the code, set TOTP as the preferred factor,
  // then complete sign-in.
  const handleMfaSetup = async () => {
    setError('');
    setIsInProgress(true);
    try {
      await Auth.verifyTotpToken(mfaUser, mfaCode.replace(/\D/g, ''));
      await Auth.setPreferredMFA(mfaUser, 'TOTP');
      await afterSignedIn();
    } catch (err: any) {
      setIsInProgress(false);
      setError('That code didn’t match. Check your authenticator app and enter the current 6-digit code.');
    }
  };

  const handleNewPassword = async () => {
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsInProgress(true);
    try {
      const completed = await Auth.completeNewPassword(userChallenge, newPassword);
      setUserChallenge(null);
      // Completing the new password may chain into MFA setup if the pool
      // requires it; route through the shared handler rather than assuming.
      await handleSignInResult(completed);
    } catch (err: any) {
      setIsInProgress(false);
      setError(withPasswordHelp(err));
    }
  };

  // Cognito password-policy errors are opaque; append the human-readable rules.
  const withPasswordHelp = (err: any): React.ReactNode => {
    const base: React.ReactNode = err?.message || 'Failed to set new password';
    if (err?.code === 'InvalidPasswordException' || (err?.message && err.message.toLowerCase().includes('password'))) {
      return (
        <div>
          <div>{err.message}</div>
          <div className="mt-2 fw-bold" style={{ fontSize: '0.9em' }}>Password requirements:</div>
          <ul className="mb-0 mt-1 ps-3" style={{ textAlign: 'left', fontSize: '0.85em' }}>
            <li>Minimum 8 characters long</li>
            <li>At least 1 number</li>
            <li>At least 1 special character</li>
            <li>At least 1 uppercase letter</li>
            <li>At least 1 lowercase letter</li>
          </ul>
        </div>
      );
    }
    return base;
  };

  // Enter the forgot-password flow from the login form.
  const startForgot = () => {
    setError('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setForgotMode('request');
  };

  // Leave the forgot-password flow, back to the standard login form.
  const cancelForgot = () => {
    setError('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setIsInProgress(false);
    setForgotMode(null);
  };

  // Ask Cognito to email a verification code to the account's verified address.
  const handleForgotRequest = async () => {
    setError('');
    if (!username.trim()) {
      setError('Enter your username or email first.');
      return;
    }
    setIsInProgress(true);
    try {
      await Auth.forgotPassword(username.trim());
      setForgotMode('reset');
      setIsInProgress(false);
    } catch (err: any) {
      setIsInProgress(false);
      // Cognito returns UserNotFoundException for unknown users; avoid
      // confirming whether an account exists — surface a neutral message.
      if (err?.code === 'UserNotFoundException' || err?.code === 'InvalidParameterException') {
        setForgotMode('reset');
        return;
      }
      if (err?.code === 'LimitExceededException') {
        setError('Too many attempts. Please wait a while and try again.');
        return;
      }
      setError(err?.message || 'Could not send a reset code. Please try again.');
    }
  };

  // Submit the emailed code plus the chosen new password.
  const handleForgotSubmit = async () => {
    setError('');
    if (!resetCode.trim()) {
      setError('Enter the verification code from your email.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setIsInProgress(true);
    try {
      await Auth.forgotPasswordSubmit(username.trim(), resetCode.trim(), newPassword);
      // Success: drop back to the login form with a one-off confirmation.
      cancelForgot();
      setPassword('');
      setNotice('Your password has been changed. Please sign in with your new password.');
    } catch (err: any) {
      setIsInProgress(false);
      const code = err?.code || err?.name;
      if (code === 'CodeMismatchException') {
        setError('That code is incorrect. Check the latest email and try again.');
        return;
      }
      if (code === 'ExpiredCodeException') {
        setError('That code has expired. Go back and request a new one.');
        return;
      }
      setError(withPasswordHelp(err));
    }
  };

  const renderForm = () => {
    // Forgot password — request a code.
    if (forgotMode === 'request') {
      return (
        <form onSubmit={(e) => { e.preventDefault(); handleForgotRequest(); }}>
          <h2 className='font-primary'>Reset your password</h2>
          <p style={{ fontSize: '0.9em' }}>Enter your email and we’ll send you a verification code.</p>
          <input className="form-control mb-3" type="email" placeholder="Email" disabled={isInProgress}
            value={username} onChange={(e) => setUsername(e.target.value)} />
          <button type="submit" className="btn btn-primary w-100" disabled={isInProgress}>Send code</button>
          <button type="button" className="btn btn-link w-100 mt-2" disabled={isInProgress} onClick={cancelForgot}>
            Back to login
          </button>
        </form>
      );
    }

    // Forgot password — enter code + new password.
    if (forgotMode === 'reset') {
      return (
        <form onSubmit={(e) => { e.preventDefault(); handleForgotSubmit(); }}>
          <h2 className='font-primary'>Enter code &amp; new password</h2>
          <p style={{ fontSize: '0.9em' }}>We’ve sent a verification code to your registered email address. Enter it below along with your new password.</p>
          <input className="form-control mb-3" inputMode="numeric" autoComplete="one-time-code"
            placeholder="Verification code" disabled={isInProgress}
            value={resetCode} onChange={(e) => setResetCode(e.target.value)} />
          <input className="form-control mb-3" type="password" placeholder="New Password" disabled={isInProgress}
            value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          <input className="form-control mb-3" type="password" placeholder="Confirm Password" disabled={isInProgress}
            value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
          <button type="submit" className="btn btn-primary w-100" disabled={isInProgress}>Set new password</button>
          <button type="button" className="btn btn-link w-100 mt-2" disabled={isInProgress} onClick={() => { setError(''); setForgotMode('request'); }}>
            Didn’t get a code? Try again
          </button>
        </form>
      );
    }

    // First-time MFA enrolment (QR + verify).
    if (mfaMode === 'setup') {
      return (
        <form onSubmit={(e) => { e.preventDefault(); handleMfaSetup(); }}>
          <h2 className='font-primary'>Set up authentication</h2>
          <p style={{ fontSize: '0.9em' }}>
            Scan this code with an authenticator app (Google Authenticator, Microsoft
            Authenticator or Authy) <strong>on your own phone</strong>, then enter the
            6-digit code it shows.
          </p>
          <div className="d-flex justify-content-center mb-3">
            {totpUri && <QRCodeSVG value={totpUri} size={180} />}
          </div>
          {totpSecret && (
            <p style={{ fontSize: '0.8em' }} className="mb-2">
              Can’t scan? Enter this key manually:<br />
              <code style={{ wordBreak: 'break-all' }}>{totpSecret}</code>
            </p>
          )}
          <input ref={codeInputRef} className="form-control mb-3" inputMode="numeric" autoComplete="one-time-code"
            placeholder="6-digit code" disabled={isInProgress} value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)} />
          <button type="submit" className="btn btn-primary w-100" disabled={isInProgress || mfaCode.trim().length < 6}>
            Verify &amp; continue
          </button>
        </form>
      );
    }

    // Enrolled user entering their code.
    if (mfaMode === 'verify') {
      return (
        <form onSubmit={(e) => { e.preventDefault(); handleMfaVerify(); }}>
          <h2 className='font-primary'>Enter your code</h2>
          <p style={{ fontSize: '0.9em' }}>Enter the 6-digit code from your authenticator app.</p>
          <input ref={codeInputRef} className="form-control mb-3" inputMode="numeric" autoComplete="one-time-code"
            placeholder="6-digit code" disabled={isInProgress} value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)} />
          <button type="submit" className="btn btn-primary w-100" disabled={isInProgress || mfaCode.trim().length < 6}>
            Verify
          </button>
        </form>
      );
    }

    // First-login forced password change.
    if (userChallenge) {
      return (
        <form onSubmit={(e) => { e.preventDefault(); handleNewPassword(); }}>
          <h2 className='font-primary'>Set a New Password</h2>
          <input className="form-control mb-3" type="password" placeholder="New Password" disabled={isInProgress} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          <input className="form-control mb-3" type="password" placeholder="Confirm Password" disabled={isInProgress} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
          <button type="submit" className="btn btn-primary mt-2" disabled={isInProgress}>Submit</button>
        </form>
      );
    }

    // Default: username + password.
    return (
      <form onSubmit={(e) => { e.preventDefault(); doLogin(); }}>
        <h2 className='font-primary'>Login</h2>
        <input className="form-control mb-3" type="email" placeholder="Email" disabled={isInProgress} value={username} onChange={(e) => setUsername(e.target.value)} />
        <input className="form-control mb-3" placeholder="Password" type="password" disabled={isInProgress} value={password} onChange={(e) => setPassword(e.target.value)} />
        <button type="submit" className="btn btn-primary w-100" disabled={isInProgress}>Login</button>
        <button type="button" className="btn btn-link w-100 mt-2" disabled={isInProgress} onClick={startForgot}>
          Forgot password?
        </button>
      </form>
    );
  };

  return (
    <div className="login-page rustle-background">
      {!isLoading && (
        <div className="login-wrapper">
          <img src={logo} alt="Company Logo" className="logo" />
          <div className="login-box">
            {notice && <div className="alert alert-warning py-2 mb-3" style={{ fontSize: '0.9em' }}>{notice}</div>}
            {renderForm()}
            {error && <div className="text-danger mt-2">{error}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export default Login
